import axios from 'axios';
import {
  FlashcardDTO,
  FlashcardCreateUpdateDTO,
  FlashcardProgresoDTO,
  FlashcardEstadisticasDTO,
  FlashcardFilters
} from '../Models/Flashcard';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export class FlashcardService {
  private baseUrl = `${API_BASE_URL}/api/flashcards`;

  /**
   * Obtener flashcards con filtros opcionales
   */
  async obtenerFlashcards(filtros: Partial<FlashcardFilters> & {
    modo?: 'NUEVAS' | 'REPASO' | 'MEZCLA';
    limit?: number;
    shuffle?: boolean;
  }): Promise<FlashcardDTO[]> {
    try {
      const params = new URLSearchParams();

      if (filtros.especialidadId) params.append('especialidadId', filtros.especialidadId.toString());
      if (filtros.temaId) params.append('temaId', filtros.temaId.toString());
      if (filtros.soloNoEstudiadas !== undefined) params.append('soloNoEstudiadas', filtros.soloNoEstudiadas.toString());
      if (filtros.usuarioId) params.append('usuarioId', filtros.usuarioId.toString());
      if (filtros.modo) params.append('modo', filtros.modo);
      if (filtros.limit && filtros.limit > 0) params.append('limit', filtros.limit.toString());
      if (filtros.shuffle) params.append('shuffle', 'true');

      const response = await axios.get(`${this.baseUrl}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener flashcards:', error);
      throw error;
    }
  }

  /**
   * Historial: últimas flashcards estudiadas por el usuario.
   */
  async obtenerHistorial(usuarioId: number, limit = 20): Promise<Array<{
    flashcardId: number;
    pregunta: string;
    especialidadNombre?: string;
    temaNombre?: string;
    vecesEstudiado: number;
    ultimaVezEstudiado: string;
  }>> {
    const params = new URLSearchParams();
    params.append('usuarioId', usuarioId.toString());
    params.append('limit', limit.toString());
    const response = await axios.get(`${this.baseUrl}/historial?${params.toString()}`);
    return response.data;
  }

  /**
   * Trae flashcards específicas por IDs (para "Repasar estas" del historial).
   */
  async obtenerPorIds(ids: number[], usuarioId?: number): Promise<FlashcardDTO[]> {
    const params = new URLSearchParams();
    params.append('ids', ids.join(','));
    if (usuarioId) params.append('usuarioId', usuarioId.toString());
    const response = await axios.get(`${this.baseUrl}/por-ids?${params.toString()}`);
    return response.data;
  }

  /**
   * Marcar una flashcard como estudiada
   */
  async marcarComoEstudiada(flashcardId: number, usuarioId: number): Promise<void> {
    try {
      const dto: FlashcardProgresoDTO = { usuarioId };
      await axios.post(`${this.baseUrl}/${flashcardId}/estudiar`, dto);
    } catch (error) {
      console.error('Error al marcar flashcard como estudiada:', error);
      throw error;
    }
  }

  /**
   * Reinicia todo el progreso de flashcards del usuario (las vuelve "nuevas").
   * Devuelve cuántas tarjetas se reiniciaron.
   */
  async reiniciarProgreso(usuarioId: number): Promise<number> {
    try {
      const response = await axios.delete<number>(`${this.baseUrl}/progreso/${usuarioId}`);
      return response.data;
    } catch (error) {
      console.error('Error al reiniciar el progreso de flashcards:', error);
      throw error;
    }
  }

  /**
   * Obtener estadísticas de progreso
   */
  async obtenerEstadisticas(
    usuarioId: number,
    especialidadId?: number,
    temaId?: number
  ): Promise<FlashcardEstadisticasDTO> {
    try {
      const params = new URLSearchParams();
      params.append('usuarioId', usuarioId.toString());
      
      if (especialidadId) {
        params.append('especialidadId', especialidadId.toString());
      }
      
      if (temaId) {
        params.append('temaId', temaId.toString());
      }

      const response = await axios.get(`${this.baseUrl}/estadisticas?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener estadísticas:', error);
      throw error;
    }
  }

  // ===============================
  // MÉTODOS PARA ADMINISTRADORES
  // ===============================

  /**
   * Crear nueva flashcard (ADMIN)
   */
  async crearFlashcard(dto: FlashcardCreateUpdateDTO): Promise<FlashcardDTO> {
    try {
      const response = await axios.post(this.baseUrl, dto);
      return response.data;
    } catch (error) {
      console.error('Error al crear flashcard:', error);
      throw error;
    }
  }

  /**
   * Actualizar flashcard existente (ADMIN)
   */
  async actualizarFlashcard(id: number, dto: FlashcardCreateUpdateDTO): Promise<FlashcardDTO> {
    try {
      const response = await axios.put(`${this.baseUrl}/${id}`, dto);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar flashcard:', error);
      throw error;
    }
  }

  /**
   * Eliminar (desactivar) flashcard (ADMIN)
   */
  async eliminarFlashcard(id: number): Promise<void> {
    try {
      await axios.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error('Error al eliminar flashcard:', error);
      throw error;
    }
  }
}

export const flashcardService = new FlashcardService();